// Where an auth link that landed on "/" should actually go.
//
// Supabase appends its tokens to the URL as a hash — `#access_token=…&type=…`
// — and two kinds of link can end up on the bare homepage:
//
//   • SIGNUP links from older app builds, whose emailRedirectTo was "/".
//   • ANY link whose redirect_to was not in Supabase's Redirect URLs
//     allow-list. Supabase silently substitutes the Site URL in that case and
//     still returns 200 with no mailer error, so nothing in the auth log says
//     the redirect was dropped. `/reset` was missing from that list until
//     Sep 25 2026, which is why every password reset for two months arrived
//     here instead: the marketing page read the hash, signed the user in, and
//     never showed a password form. 9 reset requests, 1 later sign-in.
//
// The allow-list is fixed now, so this is a backstop rather than the main
// path. It still earns its place: the redirect is built from
// window.location.origin, so `www.` and every Vercel preview host generate
// their own URL and each needs its own allow-list entry — and reset links are
// already sitting in inboxes.
//
// Pure and exported so the decision is testable without mounting the router or
// faking window.location. The caller does the navigating.

/**
 * Returns the path (including hash) to redirect to, or null to stay put.
 *
 * Only ever fires on "/" — every other route either handles its own hash or
 * has no business reading one.
 */
export const authHashRedirect = (pathname: string, hash: string): string | null => {
  if (pathname !== "/") return null;
  // No token means there is nothing to hand anywhere. A `type=` on its own is
  // not enough: the destination pages exist to consume a session, and sending
  // someone to /reset with no token just shows them the expired-link screen.
  if (!hash.includes("access_token=")) return null;
  if (hash.includes("type=signup")) return `/confirmed${hash}`;
  if (hash.includes("type=recovery")) return `/reset${hash}`;
  // Anything else (magic link, invite, email change) has no dedicated landing
  // page yet. Leave it — supabase-js still picks the session up from the hash,
  // and inventing a destination here would be guessing at a flow we don't run.
  return null;
};
